import AsyncStorage from '@react-native-async-storage/async-storage';
import { auth } from '@/lib/firebase';
import { db } from '@/lib/firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

const CLEARED_ALERTS_KEY = '@tripflow_cleared_alerts';
const scopedClearedAlertsKey = (uid: string) => `${CLEARED_ALERTS_KEY}:${uid}`;

const getActiveClearedAlertsKey = () => {
  const uid = auth.currentUser?.uid;
  return uid ? scopedClearedAlertsKey(uid) : CLEARED_ALERTS_KEY;
};

const getActiveUserId = () => auth.currentUser?.uid ?? null;

const parseRemoteClearedIds = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is string => typeof value === 'string');
};

const getRemoteClearedIds = async (uid: string): Promise<string[]> => {
  try {
    const snapshot = await getDoc(doc(db, 'users', uid));
    if (!snapshot.exists()) return [];
    const data = snapshot.data() as Record<string, any>;
    const ids = data?.preferences?.notifications?.clearedAlertIds;
    return parseRemoteClearedIds(ids);
  } catch (error) {
    console.error('[ClearedAlerts] Error reading remote cleared alerts:', error);
    return [];
  }
};

const setRemoteClearedIds = async (uid: string, ids: string[]): Promise<void> => {
  try {
    await setDoc(
      doc(db, 'users', uid),
      {
        preferences: {
          notifications: {
            clearedAlertIds: ids,
            clearedAlertsUpdatedAt: serverTimestamp(),
          },
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (error) {
    console.error('[ClearedAlerts] Error writing remote cleared alerts:', error);
  }
};

const parseIds = (raw: string | null): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
};

const getStoredClearedIds = async (): Promise<string[]> => {
  const activeKey = getActiveClearedAlertsKey();
  const scopedRaw = await AsyncStorage.getItem(activeKey);
  const scopedIds = parseIds(scopedRaw);
  const uid = getActiveUserId();
  if (!uid) {
    if (scopedIds.length > 0 || activeKey === CLEARED_ALERTS_KEY) {
      return scopedIds;
    }

    // One-time migration path: if a user-scoped key is empty, fall back to legacy global data.
    const legacyRaw = await AsyncStorage.getItem(CLEARED_ALERTS_KEY);
    const legacyIds = parseIds(legacyRaw);
    if (legacyIds.length > 0) {
      await AsyncStorage.setItem(activeKey, JSON.stringify(legacyIds));
    }
    return legacyIds;
  }

  const remoteIds = await getRemoteClearedIds(uid);

  // One-time migration path: if scoped local data is empty, fall back to legacy global data.
  const legacyRaw = scopedIds.length > 0 ? null : await AsyncStorage.getItem(CLEARED_ALERTS_KEY);
  const legacyIds = parseIds(legacyRaw);
  const localBaseline = scopedIds.length > 0 ? scopedIds : legacyIds;

  const merged = Array.from(new Set<string>([...localBaseline, ...remoteIds]));
  if (merged.length !== localBaseline.length) {
    await AsyncStorage.setItem(activeKey, JSON.stringify(merged));
  }
  if (merged.length !== remoteIds.length) {
    await setRemoteClearedIds(uid, merged);
  }
  return merged;
};

/**
 * Get all cleared alert IDs from storage
 */
export async function getClearedAlerts(): Promise<Set<string>> {
  try {
    return new Set(await getStoredClearedIds());
  } catch (error) {
    console.error('[ClearedAlerts] Error getting cleared alerts:', error);
    return new Set<string>();
  }
}

/**
 * Clear a specific alert by ID
 */
export async function clearAlert(alertId: string): Promise<void> {
  try {
    const cleared = await getClearedAlerts();
    cleared.add(alertId);
    const ids = Array.from(cleared);
    await AsyncStorage.setItem(getActiveClearedAlertsKey(), JSON.stringify(ids));
    const uid = getActiveUserId();
    if (uid) {
      await setRemoteClearedIds(uid, ids);
    }
  } catch (error) {
    console.error('[ClearedAlerts] Error clearing alert:', error);
  }
}

/**
 * Clear all alerts
 */
export async function clearAllAlerts(alertIds: string[]): Promise<void> {
  try {
    if (alertIds.length === 0) {
      return;
    }

    const cleared = await getClearedAlerts();
    alertIds.forEach((id) => cleared.add(id));
    const ids = Array.from(cleared);
    await AsyncStorage.setItem(getActiveClearedAlertsKey(), JSON.stringify(ids));
    const uid = getActiveUserId();
    if (uid) {
      await setRemoteClearedIds(uid, ids);
    }
  } catch (error) {
    console.error('[ClearedAlerts] Error clearing all alerts:', error);
  }
}

export async function resetClearedAlerts(): Promise<void> {
  try {
    await AsyncStorage.removeItem(getActiveClearedAlertsKey());
    const uid = getActiveUserId();
    if (uid) {
      await setRemoteClearedIds(uid, []);
    }
  } catch (error) {
    console.error('[ClearedAlerts] Error resetting cleared alerts:', error);
  }
}

/**
 * Remove an alert from cleared list (restore it)
 */
export async function restoreAlert(alertId: string): Promise<void> {
  try {
    const cleared = await getClearedAlerts();
    cleared.delete(alertId);
    const ids = Array.from(cleared);
    await AsyncStorage.setItem(getActiveClearedAlertsKey(), JSON.stringify(ids));
    const uid = getActiveUserId();
    if (uid) {
      await setRemoteClearedIds(uid, ids);
    }
  } catch (error) {
    console.error('[ClearedAlerts] Error restoring alert:', error);
  }
}

/**
 * Filter alerts to exclude cleared ones
 */
export async function filterClearedAlerts<T extends { id: string }>(
  alerts: T[]
): Promise<T[]> {
  const cleared = await getClearedAlerts();
  return alerts.filter((alert) => !cleared.has(alert.id));
}

